import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { AppSpin } from '@/components/atoms';
import ConsentFormSection from '@/components/organisms/ConsentFormSection';
import { a2ConsentSchema, r2ConsentSchema, ConsentFormData } from '@/schemas/consentSchema';
import { useCreateConsentMutation } from '@/hooks/main/useCreateConsentMutation';
import { useConsentDetail } from '@/hooks/main/useConsentDetail';
import { useConsentList } from '@/hooks/main/useConsentList';
import { useAppToast } from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { useConsentRouteParams } from '@/hooks/main/useConsentRouteParams';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { japaneseDateToISO } from '@/utils/dateConverter';
import { getDefaultConsentFormValues, mapDetailToFormPartial } from '@/pages/main/consent/consentFormMappers';
import {
  applyConsentApiFieldErrors,
  buildA2ConsentRequestBody,
  buildR2ConsentRequestBody,
  makeConsentInvalidHandler,
  runConsentCrossFieldChecks,
} from '@/pages/main/consent/consentFormSubmit';
import ConsentDocumentPrintModal from '@/pages/main/consent/components/ConsentDocumentPrintModal';
import ConsentFormPrintModal from '@/pages/main/consent/components/ConsentFormPrintModal';
import ConsentFormBottomBar from '@/pages/main/consent/components/ConsentFormBottomBar';
import { runAhakiCrossCheck } from '@/pages/main/consent/consentAhakiCrossCheck';
import { runLockedTreatmentMonthsCheck } from '@/pages/main/consent/consentLockedTreatmentMonthsCheck';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';

// ==================== Main Component ====================

export const CreateConsentPage: React.FC = () => {
  const { tenant } = useParams();
  const { serviceType: contextServiceType } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useAppToast();
  const { confirm, warning } = useAppModal();

  const confirmFn = useCallback(
    (message: string): Promise<boolean> =>
      new Promise(resolve => {
        confirm({
          content: message,
          okText: 'OK',
          cancelText: 'キャンセル',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    [confirm]
  );

  const alertFn = useCallback(
    (message: string): Promise<void> =>
      new Promise(resolve => {
        warning({
          content: message,
          okText: 'OK',
          onOk: () => resolve(),
        });
      }),
    [warning]
  );
  const [printDocumentType, setPrintDocumentType] = useState<1 | 2 | null>(null);
  const [consentFormPrintOpen, setConsentFormPrintOpen] = useState(false);

  const serviceType = (contextServiceType?.toLowerCase() as 'r2' | 'a2') || 'r2';
  const isA2 = serviceType.toUpperCase() === SERVICE_TYPE_A2;
  const isContinueRoute = location.pathname.endsWith('/continue');

  const derived = useConsentRouteParams();

  const latestConsentFilters = useMemo(() => ({
    page: 1,
    per_page: 1,
    sort_type: 'created_at' as const,
    sort_dir: 'desc' as const,
  }), []);

  const { data: listResponse, isLoading: isListLoading } = useConsentList(derived.patientId, latestConsentFilters);
  const listData = listResponse?.data;
  const patient = listData?.patient;
  const latestConsentId = listData?.consents?.[0]?.id ? String(listData.consents[0].id) : undefined;
  // 特殊継続同意書: only when /continue and there is no previous consent.
  const isSpecialContinue = isContinueRoute && !latestConsentId;

  const { data: sourceConsent, isLoading: isDetailLoading } = useConsentDetail({
    consentId: latestConsentId,
    patientId: derived.patientId,
    enabled: isContinueRoute && !!latestConsentId,
  });

  const defaultFormValues: Partial<ConsentFormData> = useMemo(() => getDefaultConsentFormValues(serviceType), [serviceType]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    setFocus,
    reset,
    formState: { isSubmitting: formIsSubmitting, isDirty: isFormDirty },
  } = useTrimmedForm<ConsentFormData>({
    resolver: zodResolver(isA2 ? a2ConsentSchema : r2ConsentSchema),
    defaultValues: defaultFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    criteriaMode: 'firstError',
  });
  const currentValues = watch();
  const [initialValues, setInitialValues] = React.useState<Partial<ConsentFormData>>(defaultFormValues);
  const guardValues = isFormDirty ? currentValues : (initialValues ?? currentValues);

  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(guardValues, initialValues);

  const hasInitializedContinueValues = useRef(false);

  const handleBack = useCallback(() => {
    const tenantPrefix = tenant || `${derived.siteType}/${derived.clinicNumber}`;
    const target = `/${tenantPrefix}/consents/${derived.patientId}/list`;
    if (formIsSubmitting) return;

    confirmIfChanged(() => {
      clearFormChange();
      navigate(target);
    });
  }, [
    confirmIfChanged,
    clearFormChange,
    derived.patientId,
    derived.clinicNumber,
    derived.siteType,
    formIsSubmitting,
    navigate,
    tenant,
  ]);

  useEffect(() => {
    if (!isContinueRoute || isListLoading || hasInitializedContinueValues.current) {
      return;
    }

    if (latestConsentId && isDetailLoading) {
      return;
    }

    if (isSpecialContinue) {
      const vals = {
        ...defaultFormValues,
        is_continuation: isA2,
        is_special: true,
        examine_date: '',
        ...(serviceType.toUpperCase() === SERVICE_TYPE_A2 ? { consent_date: '' } : {}),
        ...(serviceType.toUpperCase() === SERVICE_TYPE_R2 ? { first_treatment_date: '' } : {}),
      };
      setInitialValues(vals);
      reset(vals);
      hasInitializedContinueValues.current = true;
      return;
    }

    const src = sourceConsent as Record<string, unknown> | null | undefined;

    const mapped = mapDetailToFormPartial(src, serviceType);

    const prevTermToRaw = (src?.treatment_term_to as string | null | undefined) ?? '';
    const prevTermToIso = prevTermToRaw
      ? prevTermToRaw.includes('年')
        ? (japaneseDateToISO(prevTermToRaw) ?? '')
        : prevTermToRaw
      : '';
    const continueTermFrom = prevTermToIso ? dayjs(prevTermToIso).add(1, 'day').format('YYYY-MM-DD') : '';

    const vals = {
      ...defaultFormValues,
      ...mapped,
      is_continuation: true,
      is_special: false,
      examine_date: '',
      consent_date: '',
      treatment_term_from: continueTermFrom,
      treatment_term_to: '',
    };
    setInitialValues(vals);
    reset(vals);
    hasInitializedContinueValues.current = true;
  }, [
    defaultFormValues,
    isContinueRoute,
    isListLoading,
    latestConsentId,
    isDetailLoading,
    sourceConsent,
    isSpecialContinue,
    serviceType,
    reset,
  ]);

  const createMutation = useCreateConsentMutation({
    patientId: derived.patientId,
  });

  const payloadOpts = useMemo(
    () => ({ isContinueRoute, isSpecialContinue }),
    [isContinueRoute, isSpecialContinue]
  );

  const onSubmit = async (data: ConsentFormData) => {
    if (!await runConsentCrossFieldChecks(data, { isContinueRoute, serviceType, setError, confirmFn })) {
      return;
    }

    if (!await runLockedTreatmentMonthsCheck(data, {
      serviceType,
      siteType: derived.siteType,
      clinicNumber: derived.clinicNumber,
      patientId: derived.patientId,
      confirmFn,
    })) {
      return;
    }

    const ahakiOk = await runAhakiCrossCheck(data, {
      serviceType,
      siteType: derived.siteType,
      clinicNumber: derived.clinicNumber,
      patientId: derived.patientId,
      confirmFn,
      alertFn,
      onAutoSet: (field, value) => setValue(field, value, { shouldDirty: true }),
    });
    if (!ahakiOk) return;

    const onMutationSuccess = () => {
      clearFormChange();
    };

    const onMutationError = (error: unknown) => {
      applyConsentApiFieldErrors(error, setError);
      const err = error as { response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } } };
      if (err.response?.status === 422 && err.response.data?.errors) {
        toast.error('更新ができませんでした。');
      } else if (err.response?.status === 409) {
        // HIT-7357: backend caught a conflicting あはき counterpart right before writing (two-tab
        // race) — save was rejected, nothing written. Re-run the form's own check by resubmitting.
        toast.error(err.response.data?.message ?? '他の操作により更新されました。もう一度お試しください。');
      }
    };

    const body = isA2
      ? buildA2ConsentRequestBody(data, payloadOpts)
      : buildR2ConsentRequestBody(data, payloadOpts);

    createMutation.mutate(body, {
      onSuccess: onMutationSuccess,
      onError: onMutationError,
    });
  };

  const isSubmitting = formIsSubmitting || createMutation.isPending;
  if (isListLoading || (isContinueRoute && !!latestConsentId && isDetailLoading)) {
    return (
      <div className="flex justify-center p-10">
        <AppSpin size="large" />
      </div>
    );
  }

  const formId = 'consent-create-form';
  const printConsentId = Number(latestConsentId || 0);
  // Print/delete buttons always disabled on create/continue page (nothing to act on before save).
  const canPrintDocument = false;

  return (
    <div className="patient-edit-page">
      {derived.patientId && (
        <PatientPageHeader
          patient={patient}
          patientId={derived.patientId}
          tenant={tenant || ''}
          navigate={navigate}
          activeKey="consents"
          disabled={isSubmitting}
        />
      )}

      <div>
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit, makeConsentInvalidHandler(toast, setFocus))}
        >
          <ConsentFormSection
            control={control}
            watch={watch}
            setValue={setValue}
            serviceType={serviceType}
            formMode="create"
            isContinue={isContinueRoute}
            patient={patient}
            disabled={isSubmitting}
          />
        </form>
      </div>

      <ConsentFormBottomBar
        mode="create"
        isSubmitting={isSubmitting}
        formId={formId}
        onBack={handleBack}
        canPrint={canPrintDocument}
        onClickThankYouPrint={() => setPrintDocumentType(1)}
        onClickRequestPrint={() => setPrintDocumentType(2)}
        onClickConsentFormPrint={() => setConsentFormPrintOpen(true)}
      />

      {canPrintDocument && printDocumentType && (
        <ConsentDocumentPrintModal
          consentId={printConsentId}
          documentTypeId={printDocumentType}
          isOpen={true}
          onClose={() => setPrintDocumentType(null)}
        />
      )}

      {canPrintDocument && (
        <ConsentFormPrintModal
          consentId={printConsentId}
          siteType={derived.siteType}
          clinicNumber={derived.clinicNumber}
          serviceTypeHeader={isA2 ? SERVICE_TYPE_A2 : SERVICE_TYPE_R2}
          isOpen={consentFormPrintOpen}
          onClose={() => setConsentFormPrintOpen(false)}
        />
      )}
    </div>
  );
};
